function makeProxyClone(original, proxyPid, proxyId, kind = 'spouse') {
  const clone = JSON.parse(JSON.stringify(original));

  clone.pid = proxyPid;
  clone.uid = `${original.uid || original.pid}-proxy-${proxyId}`;
  clone.isProxy = true;
  clone.proxyOf = original.pid;

  // TGCLOUD-1597 — which relationship this clone exists to redraw. A SPOUSE proxy carries the
  // marriage; a DONOR proxy carries the donor edge and must NOT carry a spouse link, or the layout
  // would seat it as a partner and draw a mating line to someone it never married.
  if (kind === 'donor') {
    clone.spouseIds = [];
    clone.donorOfPids = [proxyId];
    clone.proxyKind = 'donor';
  } else {
    clone.spouseIds = [proxyId];
    clone.donorOfPids = [];
    clone.proxyKind = 'spouse';
  }

  clone.motherId = null;
  clone.fatherId = null;
  clone.childrenIds = [];
  clone.siblingIds = [];

  delete clone.mother;
  delete clone.father;
  delete clone.children;
  delete clone.twins;
  delete clone.relationship;
  delete clone.spouse;

  // A proxy is a RE-DRAWING of one person on another generation, not a second member of
  // anything — so it carries no multiple-birth identity. `siblingType` was already being
  // stripped here; TGCLOUD-1692 found the other two fields still riding along, which
  // matters because a twin set is now grouped and MOVED as one atom by
  // `_reprojectLockedOrderV3` / `_applyOrderMap` (both of which key on twinSubGroupId,
  // and rank proxies because `makeProxyClone` gives them a stable uid). A proxy would
  // have been dragged into its original's atom and seated beside it, on a generation
  // where it does not belong.
  //
  // Nothing is lost by removing them: a twin sub-group is formed from
  // `belongsToSiblingGroup` (Family.js), and a proxy has no parents and no siblingIds, so
  // it was never in a sibling group and never carried a drawn bar of its own.
  delete clone.siblingType;
  delete clone.twinSubGroupId;
  delete clone.twinRelations;

  // The original's marriage may carry a Set-Relation cross-level marker
  // (crossLevelSpouseIds) — an EXPLICIT diagonal. The clone exists precisely to
  // redraw that marriage LEVEL on the spouse's generation, so it must not
  // inherit the marker or generation assignment will refuse to seat it beside
  // its spouse (G-1-01411).
  delete clone.crossLevelSpouseIds;

  return clone;
}

export function deriveProxyNodes(records, proxies) {
  if (
    !Array.isArray(records) ||
    !Array.isArray(proxies) ||
    proxies.length === 0
  ) {
    return records || [];
  }

  const working = records.slice();

  // Synthetic proxy pids start above the highest real pid so they never collide.
  let maxPid = 0;
  for (const r of working) {
    const n = Number(r?.pid);
    if (!Number.isNaN(n)) maxPid = Math.max(maxPid, n);
  }
  let nextProxyPid = maxPid + 1;

  for (const intent of proxies) {
    const originalPid = intent?.originalPid;
    const proxyId = intent?.proxyId;
    if (originalPid == null || proxyId == null) continue;

    const idxO = working.findIndex((p) => p.pid === originalPid);
    const idxS = working.findIndex((p) => p.pid === proxyId);
    if (idxO < 0 || idxS < 0) continue;

    const original = working[idxO];
    // Only derive when the relationship being proxied genuinely still exists.
    //
    // TGCLOUD-1597 — this used to accept SPOUSE pairings only, which is why an identified donor
    // could not be proxied: the ticket asks for one precisely because a donor who is an existing
    // family member otherwise drags a line right across the pedigree. Spouse is tried FIRST, so a
    // person who is both a spouse and a donor proxies the marriage exactly as before — the
    // pre-existing behaviour wins and no saved proxy changes meaning.
    const isSpouseProxy = (original.spouseIds || []).includes(proxyId);
    const isDonorProxy =
      !isSpouseProxy && (original.donorOfPids || []).includes(proxyId);
    if (!isSpouseProxy && !isDonorProxy) continue;

    const proxyPid = nextProxyPid++;

    // A DONOR proxy is a much simpler object than a spouse proxy: there is no marriage to move and
    // no shared children to re-parent. Only the donor EDGE moves to the clone, so the long line
    // across the chart is replaced by a short one from a copy sitting next to the offspring.
    if (isDonorProxy) {
      const newOriginal = { ...original };
      newOriginal.donorOfPids = (original.donorOfPids || []).filter(
        (id) => id !== proxyId
      );
      working[idxO] = newOriginal;
      working.push(makeProxyClone(original, proxyPid, proxyId, 'donor'));
      continue;
    }

    // Children shared by the original person and this spouse — the same rule the
    // old persisted-proxy path used to decide which children move to the proxy.
    const sharedChildPids = (original.childrenIds || []).filter((childPid) => {
      const child = working.find((p) => p.pid === childPid);
      if (!child) return false;
      return (
        (child.motherId === originalPid && child.fatherId === proxyId) ||
        (child.fatherId === originalPid && child.motherId === proxyId)
      );
    });

    const newOriginal = { ...original };
    newOriginal.spouseIds = (original.spouseIds || []).filter(
      (id) => id !== proxyId
    );
    newOriginal.childrenIds = (original.childrenIds || []).filter(
      (id) => !sharedChildPids.includes(id)
    );
    working[idxO] = newOriginal;

    const spouse = working[idxS];
    const newSpouse = { ...spouse };
    newSpouse.spouseIds = Array.from(
      new Set([
        ...(spouse.spouseIds || []).filter((id) => id !== originalPid),
        proxyPid
      ])
    );
    working[idxS] = newSpouse;

    for (const childPid of sharedChildPids) {
      const cIdx = working.findIndex((p) => p.pid === childPid);
      if (cIdx < 0) continue;
      const child = { ...working[cIdx] };
      if (child.motherId === originalPid) child.motherId = proxyPid;
      if (child.fatherId === originalPid) child.fatherId = proxyPid;
      working[cIdx] = child;
    }

    const proxy = makeProxyClone(original, proxyPid, proxyId, 'spouse');
    proxy.childrenIds = sharedChildPids.slice();
    working.push(proxy);
  }

  return working;
}
